import { isZeroDecimal } from "@xola/ui-kit";
import { getMaskedLineItemName, getSortedAddonAndDemographicLineItems, isVisibleToSeller } from "common";
import { truncate } from "lodash";
import { LineItem, LineItemType, Purchase } from "types";

interface StripeCartLineItem {
    description: string;
    amount: number; // in cents
    quantity: number;
}

interface StripeDisplayInfo {
    type: "cart";
    cart: {
        line_items: StripeCartLineItem[];
        tax: number; // in cents
        total: number; // in cents
        currency: string; // lowercase
    };
}

/**
 * Builds display info for Stripe Terminal reader from purchase data.
 * Transforms purchase line items into Stripe's cart format.
 * Aggregates line items with identical names and prices, skips items with quantity = 0.
 *
 * @param {Purchase} purchase - The purchase object containing line items and amount details
 * @returns {StripeDisplayInfo} Display info formatted for Stripe Terminal setReaderDisplay API
 */
export const buildCartDisplayInfo = (purchase: Purchase): StripeDisplayInfo => {
    const lineItems: StripeCartLineItem[] = [];
    let taxTotal = 0;

    // Process all purchase items and their line items
    for (const item of purchase.items) {
        const sortedLineItems = getSortedAddonAndDemographicLineItems(item.lineItems);

        for (const lineItem of sortedLineItems) {
            // Skip line items with explicitly zero quantity (but not undefined)
            if (lineItem.quantity === 0) {
                continue;
            }

            const amount = isZeroDecimal(purchase.currency) ? lineItem.amount : Math.round(lineItem.amount * 100);

            // Aggregate TAX line items into the tax field
            if (lineItem.type === LineItemType.TAX) {
                taxTotal += amount;
                continue;
            }

            // Include FEE line items as separate line items (not aggregated into tax)
            // Include demographics, addons, adjustments, and other visible line items
            if (shouldIncludeLineItem(lineItem)) {
                // Mask coupon codes and other sensitive information for customer-facing terminal display
                const truncatedName = truncate(getMaskedLineItemName(lineItem), { length: 26 });

                // Use price for comparison, fallback to amount if price doesn't exist
                // This ensures items with same name but different unit prices stay separate
                const unitPrice =
                    lineItem.price !== undefined && lineItem.price !== null
                        ? isZeroDecimal(purchase.currency)
                            ? lineItem.price
                            : Math.round(lineItem.price * 100)
                        : amount;

                const existingItem = lineItems.find(
                    (cartItem) => cartItem.description === truncatedName && cartItem.amount === unitPrice,
                );

                const quantity = lineItem.quantity ?? 1;

                if (existingItem) {
                    // Aggregate identical items (same name and unit price)
                    existingItem.quantity += quantity;
                } else {
                    lineItems.push({
                        description: truncatedName,
                        amount: unitPrice,
                        quantity: quantity,
                    });
                }
            }
        }
    }

    const totalAmount = isZeroDecimal(purchase.currency) ? purchase.amount : Math.round(purchase.amount * 100);

    return {
        type: "cart",
        cart: {
            line_items: lineItems,
            tax: taxTotal,
            total: totalAmount,
            currency: purchase.currency.toLowerCase(),
        },
    };
};

/**
 * Determines if a line item should be displayed on the reader.
 * Excludes TAX types (aggregated separately) and relies on isVisibleToSeller for visibility.
 */
const shouldIncludeLineItem = (lineItem: LineItem): boolean => {
    return isVisibleToSeller(lineItem) && lineItem.type !== LineItemType.TAX;
};
